import { isLocationBound, picksPlace } from 'shared/lib/roles'
import { useAuthStore } from './auth'
import { useSelectedLocationStore } from './selected-location'

/**
 * The place a place-scoped screen works at, and whether the person may change it.
 *
 * A bound role — an `operator` at one Point, a `hub` worker at a hub — reads its
 * place from the signed-in account (ADR-0029), which `AppInitializers` refreshes
 * from `auth.Me` on every start; it cannot be changed from the panel, so screens
 * render no selector for it. An `admin` is global and picks the Point, shared
 * across screens through `useSelectedLocationStore`. The other global roles
 * (driver, cashier, support) neither carry a place nor pick one.
 *
 * A bound account with no place gets `locationId: null`: screens show nothing
 * place-scoped, and the selector tells them to ask an administrator.
 */
export function useLocationScope() {
	const user = useAuthStore((s) => s.user)
	const chosenLocationId = useSelectedLocationStore((s) => s.locationId)
	const setLocationId = useSelectedLocationStore((s) => s.setLocationId)

	if (isLocationBound(user.role)) {
		return {
			isBound: true,
			canPick: false,
			locationId: user.locationId ?? null,
			locationCode: user.locationCode ?? null,
			setLocationId: ignoreLocationChange,
		} as const
	}
	if (picksPlace(user.role)) {
		return {
			isBound: false,
			canPick: true,
			locationId: chosenLocationId,
			locationCode: null,
			setLocationId,
		} as const
	}
	return {
		isBound: false,
		canPick: false,
		locationId: null,
		locationCode: null,
		setLocationId: ignoreLocationChange,
	} as const
}

const ignoreLocationChange: (locationId: string | null) => void = () => {}
